import { normalizeRole, RoleS, srRoles } from "@ugg/shared/utils/role-helpers";
import { PlayerStats } from "../RoleBreakdownModule";
import { ChampionStats, ChampionDataEntry } from "../ChampStatsModule";
import { GameLogs } from "./year-in-review-playtime-helpers";
import { StatCardData } from "../StatCardModule";
import { FriendData } from "../TeamPlayersModule";
import { PingType } from "../PingStatsModule";
import { WardStatsModuleProps } from "../WardStatsModule";
import { YearInReviewData } from "@ugg/shared/api/requests/summoner-profiles/year-in-review";
import { AramChampionStats } from "../AramChampStatsModule";

interface ChampionScore {
  id: number;
  score: number;
  winRate: number;
  kda: number;
  totalGames: number;
  stats: ChampionStats;
}

//
// Game Logs
//
export function transformGameLogs(rawGameLogs: YearInReviewData): GameLogs[] {
  const standardLogs = rawGameLogs?.game_logs ? Object.values(rawGameLogs.game_logs) : [];
  const aramLogs = rawGameLogs?.aram_data?.game_logs ? Object.values(rawGameLogs.aram_data.game_logs) : [];
  const gameLogEntries = [...standardLogs, ...aramLogs];

  if (gameLogEntries.length === 0) {
    return [];
  }
  return gameLogEntries.map((entry) => {
    const [month, date, year] = entry.date.split("/");
    // normalize and pad
    const normalizedMonth = month.padStart(2, "0");
    const normalizedDate = date.padStart(2, "0");
    // rework into playtime chart format
    const normalizedDateString = `${year}-${normalizedMonth}-${normalizedDate}`;
    return {
      date: normalizedDateString, // <-- This is the critical change
      gamesPlayed: entry.games_played,
      wins: entry.wins,
      losses: entry.losses,
    };
  });
}

//
// Stat Card
//
const roundToOneDecimal = (num: number): number => {
  return Math.round(num * 10) / 10;
};

export function transformStatsForCard(rawData: YearInReviewData): StatCardData {
  const round = roundToOneDecimal;
  return {
    games: rawData.total_games,
    wins: rawData.wins,
    timePlayed: rawData.time_played,
    kda: round(rawData.kda.avg),
    avgKills: round(rawData.kills.avg),
    avgDeaths: round(rawData.deaths.avg),
    avgAssists: round(rawData.assists.avg),
    avgCreepScore: round(rawData.cs_per_min.avg * 60),
    avgGold: round(rawData.gold_per_min.avg * 60),
    avgWards: round(rawData.wards.avg.total_wards_placed),
    avgTowers: round(rawData.objectives.avg.tower_kills),
    avgDragons: round(rawData.objectives.avg.dragons_killed),
    avgBarons: round(rawData.objectives.avg.baron_kills),
    fastestTower: rawData.fastest_tower / 1000,
    fastestDragon: rawData.fastest_dragon / 1000,
  };
}

export function transformAramStatsForCard(rawData: YearInReviewData): StatCardData {
  const round = roundToOneDecimal;
  return {
    games: rawData.aram_data.total_games,
    wins: rawData.aram_data.wins,
    timePlayed: rawData.aram_data.time_played,
    kda: round(rawData.aram_data.kda.avg),
    avgKills: round(rawData.aram_data.kills.avg),
    avgDeaths: round(rawData.aram_data.deaths.avg),
    avgAssists: round(rawData.aram_data.assists.avg),
    avgCreepScore: round(rawData.aram_data.cs_per_min.avg * 60),
    avgGold: round(rawData.aram_data.gold_per_min.avg * 60),
    avgWards: 0,
    avgTowers: 0,
    avgDragons: 0,
    avgBarons: 0,
    fastestTower: 0,
    fastestDragon: 0,
  };
}

//
// Role Breakdown
//

const calculateDpm = (damageDealt: number, timePlayedSec: number, totalGames: number): number => {
  return timePlayedSec > 0 ? ((damageDealt * totalGames) / timePlayedSec) * 60 : 0;
};

export function transformRoleStats(rawData: YearInReviewData): PlayerStats {
  const playerStats: PlayerStats = {};
  const round = roundToOneDecimal;

  for (const jsonRoleKey in rawData.role_data) {
    const enumRole = normalizeRole(jsonRoleKey.toLowerCase()) as RoleS;

    if (srRoles.includes(enumRole)) {
      const roleData = rawData.role_data[jsonRoleKey];

      playerStats[enumRole] = {
        kda: round(roleData.kda.avg),
        csMin: round(roleData.cs_per_min.avg * 60),
        goldMin: round(roleData.gold_per_min.avg * 60),
        dmgMitigated: round(roleData.dmg_mitigated),
        healing: round(roleData.healing),
        dmgMin: round(calculateDpm(roleData.damage_dealt, roleData.time_played, roleData.total_games)),
        wardsGame: round(rawData.wards.avg.total_wards_placed),
      };
    }
  }
  return playerStats;
}

//
// Friends
//

export function transformTopFriends(rawFriendData: YearInReviewData): FriendData[] {
  const topFriends = [...rawFriendData.friend_data].sort((a, b) => b.total_games - a.total_games).slice(0, 3); // grab top 3

  const ICON_BASE_URL = "https://ddragon.leagueoflegends.com/cdn/15.23.1/img/profileicon/";
  return topFriends.map((friend) => ({
    losses: friend.losses,
    img: ICON_BASE_URL + `${friend.profile_icon_id}.png`,
    time_played: friend.time_played,
    total_games: friend.total_games,
    wins: friend.wins,
    username: `${friend.riot_user_name}#${friend.riot_tag_line}`,
    lp: friend.lp_gain,
  }));
}

//
// ChampionStats
//
const WIN_RATE_WEIGHT = 200;
const TOTAL_GAMES_WEIGHT = 1.5;
const KDA_WEIGHT = 0.1;

const calculateChampionScore = (id: number, data: any): ChampionScore => {
  // Calculate Win Rate
  const winRate = data.wins / data.total_games;
  const kda = data.kda.avg;
  const totalGames = data.total_games;

  const compositeScore = winRate * WIN_RATE_WEIGHT + data.total_games * TOTAL_GAMES_WEIGHT + data.kda.avg * KDA_WEIGHT;

  return {
    id,
    score: compositeScore,
    winRate,
    kda,
    totalGames,
    stats: {
      wins: Math.round((data.wins / data.total_games) * 100),
      totalGames: data.total_games,
      mostKills: data.kills.max,
      mostDeaths: data.deaths.max,
      avgKills: roundToOneDecimal(data.kills.avg),
      avgDeaths: roundToOneDecimal(data.deaths.avg),
      avgAssists: roundToOneDecimal(data.assists.avg),
      doubleKills: Math.round(data.multikills.avg.double_kills * data.total_games),
      tripleKills: Math.round(data.multikills.avg.triple_kills * data.total_games),
      quadraKills: Math.round(data.multikills.avg.quadra_kills * data.total_games),
      pentakills: Math.round(data.multikills.avg.penta_kills * data.total_games),
    },
  };
};

export function transformChampionRanking(rawData: YearInReviewData): ChampionDataEntry[] {
  const scoredChampions: ChampionScore[] = []; 
  const data=rawData.champion_data;
  if (!data || Object.keys(data).length === 0) {
    return [];
  }
  // 1. Calculate Score for each champion and filter for reliability
  for (const id in rawData.champion_data) {
    const data = rawData.champion_data[id];
    const championId = parseInt(id, 10);
    scoredChampions.push(calculateChampionScore(championId, data));
  }

  if (scoredChampions.length < 3) {
    console.warn(`Only found ${scoredChampions.length} champions with ${5}+ games. Cannot determine top 3.`);
  }

  //sort
  const sortedChampionsByGames = scoredChampions.sort((a, b) => b.totalGames - a.totalGames);
  const pocketPick = sortedChampionsByGames[0];

  const removedPocketPick = sortedChampionsByGames.filter((index) => index.id != pocketPick.id);
  const sortedChampions = removedPocketPick.sort((a, b) => b.score - a.score);

  const secondBestChamp = sortedChampions[0];
  const worstChamp = sortedChampions[sortedChampions.length - 1];

  // map
  const finalChampionData: ChampionDataEntry[] = [];

  finalChampionData.push({
    id: String(pocketPick.id),
    stats: pocketPick.stats,
  });
  finalChampionData.push({
    id: String(secondBestChamp.id),
    stats: secondBestChamp.stats,
  });
  finalChampionData.push({
    id: String(worstChamp.id),
    stats: worstChamp.stats,
  });
  return finalChampionData;
}

//
//  Ping Stats
//
const SNAKE_TO_PING_MAP: Record<string, PingType> = {
  all_in_pings: "allin",
  assist_me_pings: "assist",
  enemy_vision_pings: "enemyvision",
  get_back_pings: "retreat",
  need_vision_pings: "vision",
  on_my_way_pings: "omw",
  push_pings: "push",
  question_pings: "missing",
};

export function transformPingStats(rawData: YearInReviewData): Record<PingType, number> {
  const pingStats: Record<PingType, number> = {
    allin: 0,
    retreat: 0,
    omw: 0,
    push: 0,
    assist: 0,
    vision: 0,
    missing: 0,
    enemyvision: 0,
  };
  const aggregatePings = (averages: Record<string, number> | undefined, gameCount: number) => {
    if (!averages || !gameCount) return;

    for (const key in averages) {
      if (key in SNAKE_TO_PING_MAP) {
        const snakeKey = SNAKE_TO_PING_MAP[key as keyof typeof SNAKE_TO_PING_MAP];
        const averageValue = averages[key as keyof typeof averages];
        const totalCount = Math.round(averageValue * gameCount);
        pingStats[snakeKey] += totalCount;
      }
    }
  };
  aggregatePings(rawData.pings?.avg, rawData.total_games);
  const aramGameCount = rawData.aram_data?.total_games || 0;
  aggregatePings(rawData.aram_data?.pings?.avg, aramGameCount);
  return pingStats;
}

export function transformWardStats(rawData: YearInReviewData): WardStatsModuleProps {
  const rawAverages = rawData.wards.avg;

  return {
    avg: Math.round(rawAverages.vision_score),
    stealth: Math.round(rawAverages.normal_wards_placed * rawData.total_games),
    farsight: Math.round(rawAverages.blue_wards_placed * rawData.total_games),
    control: Math.round(rawAverages.control_wards_placed * rawData.total_games),
  };
}

export function transformAramChampStats(rawData: YearInReviewData): AramChampionStats[] {
  const aramChampions: AramChampionStats[] = [];
  const championDataMap = rawData.aram_data.champion_data;
  for (const id in championDataMap) {
    if (Object.prototype.hasOwnProperty.call(championDataMap, id)) {
      const data = championDataMap[id];
      aramChampions.push({
        id: id,
        games: data.total_games,
        wins: data.wins,
      });
    }
  }
  const sortedChampions = aramChampions.sort((a, b) => b.games - a.games);

  return sortedChampions.slice(0, 5);
}
